import { useSelector } from "react-redux";
import { selectSelectedClientIds } from "../../../../redux/slices/globalSelectorSlice";
import {
  selectAvailableClients,
  selectUserId,
  selectUserRoles,
} from "../../../../redux/slices/userSlice";
import { ServerSideDataGrid } from "../../../../common/components/serverSideDataGrid/serverSideDataGrid";
import { useCallback, useMemo, useRef, useState } from "react";
import K from "../../../../utilities/constants";
import { Col, message } from "antd";
import { getColumns } from "./columns";
import { RecruiterGoals } from "../../../../redux/models/recruiterGoals";
import { DataTableLayout } from "../../../../common/components/dataTableLayout/dataTableLayout";
import { CreateGoalsButton } from "./createGoalsButton/createGoalsButton";
import { differenceWith } from "lodash";
import { isPermissionPresent } from "../../../../utilities/generalUtility";

export const RecruiterGoalsContainer = () => {
  const selectedClientIds = useSelector(selectSelectedClientIds);
  const accessibleClients = useSelector(selectAvailableClients);
  const userId = useSelector(selectUserId);
  const userRoles = useSelector(selectUserRoles);

  const gridRef = useRef(null);

  const [clientIdsWithGoals, setClientIdsWithGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  const clientsWithoutGoals = differenceWith(
    accessibleClients,
    clientIdsWithGoals,
    (a, b) => a.id === b,
  );

  const isCreateButtonDisabled =
    loading ||
    !clientsWithoutGoals.length ||
    !isPermissionPresent(K.Permissions.SuperAdmin, userRoles);

  const onGoalsCreated = (goal) => {
    if (!clientIdsWithGoals.length) {
      gridRef.current.api.hideOverlay();
    }

    setClientIdsWithGoals((data) => [...data, goal.clientId]);
    gridRef.current.api.applyServerSideTransaction({
      add: [goal],
    });
  };

  const onGoalsUpdated = (goal) => {
    gridRef.current.api.applyServerSideTransaction({
      update: [goal],
    });
    gridRef.current.api.refreshCells({ force: true });
  };

  const onGoalsDeleted = (goal) => {
    setClientIdsWithGoals((prev) =>
      prev.filter((clientId) => clientId !== goal.clientId),
    );
    gridRef.current.api.applyServerSideTransaction({
      remove: [goal],
    });
  };

  const columns = useMemo(
    () => getColumns(userRoles, onGoalsUpdated, onGoalsDeleted),
    [userRoles],
  );

  const getServerSideData = useCallback(
    (options) => {
      return RecruiterGoals.getAll({
        ...options,
        client: selectedClientIds,
      });
    },
    [selectedClientIds],
  );

  const onDataFetchSuccess = useCallback((data) => {
    setLoading(false);
    setClientIdsWithGoals(data.rowData.map((g) => g.clientId));
  }, []);

  const onDataFetchFailed = useCallback(
    (err) => message.error(err.message),
    [],
  );

  return (
    <DataTableLayout
      searchable={false}
      actions={[
        {
          permissions: [K.Permissions.SuperAdmin],
          renderer: () => (
            <Col>
              <CreateGoalsButton
                clients={clientsWithoutGoals}
                disabled={isCreateButtonDisabled}
                onSuccess={onGoalsCreated}
              />
            </Col>
          ),
        },
      ]}
    >
      <ServerSideDataGrid
        ref={gridRef}
        userId={userId}
        configKey={K.AgGridTable.Keys.RecruiterGoalsVisibleColumn}
        getData={getServerSideData}
        defaultColumnConfig={columns}
        onDataFetchSuccess={onDataFetchSuccess}
        onDataFetchFailed={onDataFetchFailed}
      />
    </DataTableLayout>
  );
};
